/**
 * WebSocket Hook
 * Manages WebSocket connection lifecycle, reconnection logic, and message routing.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import type { WebSocketMessage, UserInputMessage, ControlMessage } from '@/types/messages';

export const ConnectionStatus = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  ERROR: 'error',
} as const;

export type ConnectionStatusValue = (typeof ConnectionStatus)[keyof typeof ConnectionStatus];

interface UseWebSocketOptions {
  url: string;
  avatarId?: string;
  token?: string;
  reconnectAttempts?: number;
  reconnectDelay?: number;
  onMessage?: (message: WebSocketMessage) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Event) => void;
}

interface UseWebSocketReturn {
  status: ConnectionStatusValue;
  sendMessage: (message: UserInputMessage | ControlMessage) => void;
  connect: () => void;
  disconnect: () => void;
  isConnected: boolean;
}

export const useWebSocket = ({
  url,
  avatarId = 'lisa',
  token,
  reconnectAttempts = 5,
  reconnectDelay = 2000,
  onMessage,
  onConnect,
  onDisconnect,
  onError,
}: UseWebSocketOptions): UseWebSocketReturn => {
  const [status, setStatus] = useState<ConnectionStatusValue>(ConnectionStatus.DISCONNECTED);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectCountRef = useRef(0);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const shouldReconnectRef = useRef(true);
  const connectCallbackRef = useRef<(() => void) | null>(null);

  const disconnect = useCallback(() => {
    shouldReconnectRef.current = false;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setStatus(ConnectionStatus.DISCONNECTED);
  }, []);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    setStatus(ConnectionStatus.CONNECTING);

    try {
      // Add avatar_id as query parameter
      const wsUrlWithAvatar = `${url}?avatar_id=${encodeURIComponent(avatarId)}`;
      const ws = new WebSocket(wsUrlWithAvatar);
      wsRef.current = ws;

      ws.onopen = () => {
        // Send authentication message as first message if token provided
        if (token) {
          ws.send(JSON.stringify({
            type: 'auth',
            token: token,
          }));
          console.log('Authentication message sent to WebSocket');
        }

        setStatus(ConnectionStatus.CONNECTED);
        reconnectCountRef.current = 0;
        onConnect?.();
      };

      ws.onmessage = (event: MessageEvent) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          onMessage?.(message);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      ws.onerror = (error: Event) => {
        console.error('WebSocket error:', error);
        setStatus(ConnectionStatus.ERROR);
        onError?.(error);
      };

      ws.onclose = () => {
        setStatus(ConnectionStatus.DISCONNECTED);
        wsRef.current = null;
        onDisconnect?.();

        // Attempt reconnection
        if (shouldReconnectRef.current && reconnectCountRef.current < reconnectAttempts) {
          reconnectCountRef.current += 1;
          const delay = reconnectDelay * reconnectCountRef.current;
          setStatus(ConnectionStatus.RECONNECTING);

          reconnectTimeoutRef.current = window.setTimeout(() => {
            connectCallbackRef.current?.();
          }, delay);
        } else if (reconnectCountRef.current >= reconnectAttempts) {
          console.error('Max reconnection attempts reached');
          setStatus(ConnectionStatus.ERROR);
        }
      };
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      setStatus(ConnectionStatus.ERROR);
    }
  }, [
    url,
    avatarId,
    token,
    reconnectAttempts,
    reconnectDelay,
    onMessage,
    onConnect,
    onDisconnect,
    onError,
  ]);

  // Store connect callback in ref for use in reconnection
  useEffect(() => {
    connectCallbackRef.current = connect;
  }, [connect]);

  const sendMessage = useCallback((message: UserInputMessage | ControlMessage) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    } else {
      console.error('Cannot send message: WebSocket is not connected');
    }
  }, []);

  // Auto-connect on mount and reconnect when avatarId or url changes
  useEffect(() => {
    shouldReconnectRef.current = true;

    // If already connected, disconnect first before reconnecting with new avatar
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.close();
    }

    // Small delay to ensure clean disconnect before reconnect
    const timeoutId = window.setTimeout(() => {
      connectCallbackRef.current?.();
    }, 100);

    // Cleanup on unmount or before next connection
    return () => {
      window.clearTimeout(timeoutId);
      shouldReconnectRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [avatarId, url]);
  return {
    status,
    sendMessage,
    connect,
    disconnect,
    isConnected: status === ConnectionStatus.CONNECTED,
  };
};
